import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import type { JSX, ReactNode } from 'react'
import { ApiError } from './api.ts'

/**
 * ══ CẤU HÌNH LỚP TRUY VẤN (KIEN-TRUC §12.2) ═══════════════════════════════════════════════════
 * Bốn giá trị dưới đây đều là quyết định về NGHIỆP VỤ, không phải mặc định gõ cho có.
 */
/**
 * ⚠ KHÔNG thử lại lỗi 4xx. Mặc định của thư viện là thử lại 3 lần cho MỌI lỗi — với 403 ("không có
 *   quyền") hay 409 (xung đột ghi) thì thử lại là vô nghĩa và chỉ bắt người dùng chờ thêm ba nhịp
 *   trước khi thấy thông báo. Lỗi mạng và 5xx thì thử lại có ích.
 *
 * Xuất ra để kiểm được — đây là một QUYẾT ĐỊNH, không phải một tuỳ chọn gõ cho có.
 */
export const shouldRetry = (failureCount: number, error: unknown): boolean => {
  if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false
  return failureCount < 2
}

const client = new QueryClient({
  defaultOptions: {
    queries: {
      /**
       * ⚠ 30 GIÂY, không phải 0 và cũng không phải 5 phút.
       *  · 0 (mặc định của thư viện) thì mỗi lần đổi tab là một lần gọi lại máy chủ — đúng bằng
       *    hành vi cũ, tức là không được gì.
       *  · Dài hơn thì kế toán sửa một danh mục ở tab này, mở tab kia vẫn thấy số cũ, và họ sửa
       *    lại lần nữa. Trong danh mục kế toán, sửa hai lần là hai dòng.
       * 30 giây đủ để một lần thao tác qua lại giữa các tab không gọi lại, và đủ ngắn để không ai
       * kịp tin vào một con số đã cũ.
       */
      staleTime: 30_000,
      retry: shouldRetry,
      /**
       * ⚠ TẮT tự gọi lại khi cửa sổ lấy lại tiêu điểm. Kế toán làm việc với hai cửa sổ (rs-erp và
       *   MISA) và chuyển qua lại liên tục; mỗi lần chuyển mà gọi lại toàn bộ danh sách là vừa
       *   tốn vừa làm bảng nhấp nháy giữa lúc họ đang đọc.
       */
      refetchOnWindowFocus: false,
    },
  },
})

export const QueryProvider = ({ children }: { children: ReactNode }): JSX.Element =>
  <QueryClientProvider client={client}>{children}</QueryClientProvider>

/** Chỉ dùng trong test: dựng một kho cache RIÊNG cho mỗi ca, không dùng chung kho toàn cục. */
export const makeTestQueryClient = (): QueryClient => new QueryClient({
  // Không thử lại và không giữ cache giữa các ca — nếu không, một ca "máy chủ lỗi" sẽ chờ ba nhịp
  // thử lại rồi hết giờ, và một ca sau đó có thể đọc trúng dữ liệu ca trước để lại.
  defaultOptions: { queries: { retry: false, staleTime: 0, gcTime: 0 } },
})
